import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Botao, Cartao, Selo, Vazio, Entrada } from "@notes/ui";
import { Search, Quote, Mic, Sparkles, ArrowUp } from "lucide-react";
import { Pagina, Secao, Erro } from "../shell/Shell.js";
import { trpc, mensagemDeErro } from "../lib/api.js";
import { marcaDeTempo } from "../lib/formato.js";

type Fonte = { reuniaoId: string; reuniaoTitulo: string; inicioMs: number; trecho: string };
type Troca = {
  pergunta: string;
  resposta?: { semDado: boolean; texto: string; fontes: Fonte[]; buscaSemantica: boolean } | undefined;
  erro?: string | undefined;
};

/**
 * PERGUNTAR AO ACERVO.
 *
 * Conversa com histórico, e não um campo que apaga a resposta anterior: quem
 * procura em conversa faz três perguntas seguidas sobre o mesmo assunto, e
 * perder a resposta anterior obriga a começar de novo.
 *
 * A resposta é a FONTE, nunca um parágrafo bonito: cada trecho abre a conversa
 * no minuto exato. Resposta sem rastro é boato.
 */
export function Perguntar() {
  const [texto, setTexto] = useState("");
  const [historico, setHistorico] = useState<Troca[]>([]);
  const fim = useRef<HTMLDivElement>(null);

  const sugestoes = useQuery({ queryKey: ["acervo", "sugestoes"], queryFn: () => trpc.acervo.sugestoes.query() });
  const exemplos = sugestoes.data?.exemplos ?? [];

  const perguntar = useMutation({
    mutationFn: (t: string) => trpc.acervo.perguntar.mutate({ texto: t }),
    onMutate: (t) => { setHistorico((h) => [...h, { pergunta: t }]); },
    onSuccess: (r) => {
      setHistorico((h) => h.map((x, i) => (i === h.length - 1 ? { ...x, resposta: r } : x)));
    },
    onError: (e) => {
      setHistorico((h) => h.map((x, i) => (i === h.length - 1 ? { ...x, erro: mensagemDeErro(e) } : x)));
    },
  });

  useEffect(() => { fim.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [historico]);

  const enviar = (t: string) => {
    const limpo = t.trim();
    if (limpo.length < 3 || perguntar.isPending) return;
    setTexto("");
    perguntar.mutate(limpo);
  };

  const semSentido = historico.some((h) => h.resposta && !h.resposta.buscaSemantica);

  return (
    <Pagina
      titulo="Perguntar ao acervo"
      largura="estreita"
      descricao="Procure em tudo que já foi conversado. A resposta vem com o trecho e o minuto."
    >
      {semSentido && (
        <Secao ordem={0}>
          <Cartao className="enotes-chamada" data-tom="atencao">
            <span className="enotes-chamada-icone" aria-hidden><Sparkles size={18} /></span>
            <div>
              <p className="enotes-chamada-titulo">A busca por sentido não está ligada nesta agência</p>
              <p className="enotes-chamada-sub">
                Por enquanto a procura é por palavra, então vale usar os mesmos termos que apareceram na conversa.
              </p>
            </div>
          </Cartao>
        </Secao>
      )}

      {historico.length === 0 ? (
        <Secao ordem={1}>
          <Vazio
            titulo="Pergunte como você perguntaria a um colega"
            descricao="Tudo que foi dito nas conversas da agência está indexado. A resposta traz o trecho e a reunião."
            icone={<Search size={28} />}
          />
          {exemplos.length > 0 && (
            <div className="enotes-exemplos">
              {exemplos.map((ex: string) => (
                <button key={ex} type="button" className="enotes-exemplo" onClick={() => enviar(ex)}>{ex}</button>
              ))}
            </div>
          )}
        </Secao>
      ) : (
        <div className="enotes-conversa">
          {historico.map((t, i) => (
            <div key={i} className="enotes-troca surge">
              <p className="enotes-pergunta"><span className="eyebrow">você</span>{t.pergunta}</p>

              {t.erro && <Erro mensagem={t.erro} />}

              {!t.resposta && !t.erro && (
                <p className="enotes-procurando"><span className="enotes-botao-giro" aria-hidden /> procurando no acervo</p>
              )}

              {t.resposta && (t.resposta.semDado ? (
                <Cartao>
                  <p className="enotes-resposta-vazia">{t.resposta.texto}</p>
                  <p className="enotes-cartao-sub">
                    Tente outras palavras, ou confira se a conversa já foi processada.
                  </p>
                </Cartao>
              ) : (
                <div className="enotes-fontes">
                  <p className="eyebrow">Onde isso aparece</p>
                  {t.resposta.fontes.map((f, n) => (
                    <Link key={n} to="/reunioes/$id" params={{ id: f.reuniaoId }} className="enotes-fonte">
                      <span className="enotes-fonte-topo">
                        <Quote size={12} aria-hidden />
                        <span className="carimbo">{marcaDeTempo(f.inicioMs)}</span>
                        <Selo tom="marca"><Mic size={11} aria-hidden />{f.reuniaoTitulo}</Selo>
                      </span>
                      <span className="enotes-fonte-trecho">{f.trecho}</span>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          ))}
          <div ref={fim} />
        </div>
      )}

      <form className="enotes-pergunta-barra" onSubmit={(e) => { e.preventDefault(); enviar(texto); }}>
        <Entrada
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="o que a Camila falou sobre orçamento"
          aria-label="Sua pergunta"
          autoFocus
        />
        <Botao variante="primario" ocupado={perguntar.isPending} aria-label="Procurar">
          <ArrowUp size={15} />
        </Botao>
      </form>
    </Pagina>
  );
}
